import React, { useState } from "react";
import {
  Typography,
  Box,
  Grid2,
  Link,
  styled,
  Tab,
  Tabs,
  IconButton,
  useTheme,
  Skeleton,
} from "@mui/material";
import styles from "styles/ProjectList.module.css";
import Highcharts from "highcharts/highstock";
import PieChart from "highcharts-react-official";
import { RxSize } from "react-icons/rx";
import { useParams } from "react-router-dom";
import { useGetDelayAnalysisQuery } from "store/slices/apis/keyClearancesDashboardSlice";
import ClearancePopup from "./ClearancePopup";

const DelayAnalysisGraph = () => {
  const theme = useTheme();
  const { projectId } = useParams();
  const [popupData, setPopupData] = useState(null);
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useGetDelayAnalysisQuery({ projectId });
  const delayData = data?.data || [];

  const delayRanges = [
    { label: "Less than 30 days", color: "#3E756D", className: styles.lowdot },
    { label: "31 to 60 days", color: "#FFBF00", className: styles.meddot },
    { label: "More than 60 days", color: "#D64733", className: styles.hvacdot },
  ];

  const tempDelayData = {};
  delayData.map((item) => {
    tempDelayData[item?.range] = item;
  });

  const validDelayRanges = delayRanges.filter(
    (data) => tempDelayData[data?.label]
  );

  const analysisData = validDelayRanges.map((item) => ({
    ...item,
    count: tempDelayData[item?.label].count,
    percentage: tempDelayData[item?.label].percentage,
  }));

  const options = {
    chart: {
      type: "pie",
      width: 240,
      height: 150,
      borderRadius: "50%",
      backgroundColor: "transparent",
    },
    title: {
      text: "",
    },
    subtitle: {
      useHTML: true,
      // text: `<div style="width: 35px; height: 26px; background-color: white; border-radius: 40%; padding-top: 5px; padding-left: 2px; box-shadow: 0 0 4px #f3f3f3;">${analysisData.reduce((sum, item) => sum + item.percentage, 0)}%</div>`,
      text: ``,
      align: "center",
      verticalAlign: "middle",
      style: {
        fontSize: "12px",
        fontWeight: "500",
        color: "#414D55",
        lineHeight: "20px",
        letterSpacing: "0.5px",
      },
      y: -65,
      x: 86,
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        allowPointSelect: false,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "{point.y}%",
          style: {
            fontSize: "12px",
            fontWeight: "500",
            color: "#000",
            fill: "#000",
            lineHeight: "20px",
            letterSpacing: "0.5px",
          },
          distance: -1,
        },
        showInLegend: false,
        innerSize: "50%",
        startAngle: 15,
        borderRadius: 0,
        borderWidth: 0,
        point: {
          events: {
            click: function () {
              let delayRange = { delayDaysMin: 0, delayDaysMax: 30 };
              switch (this.name) {
                case "Less than 30 days":
                  delayRange = { delayDaysMin: 0, delayDaysMax: 30 };
                  break;
                case "31 to 60 days":
                  delayRange = { delayDaysMin: 31, delayDaysMax: 60 };
                  break;
                case "More than 60 days":
                  delayRange = { delayDaysMin: 60, delayDaysMax: 1000 };
                  break;
              }
              setPopupData({
                name: this.name,
                value: delayRange,
              });
              setOpen(true);
            },
          },
        },
      },
    },
    series: [
      {
        name: "",
        color: "#006600",
        lineWidth: 1,
        marker: {
          enabled: false,
          symbol: "circle",
          radius: 3,
          states: {
            hover: {
              enabled: true,
              lineWidth: 1,
            },
          },
        },
        data: analysisData.map((item) => ({
          y: item.percentage,
          value: item.percentage,
          color: item.color,
          sliced: false,
          selected: false,
          name: item.label,
        })),
      },
    ],
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        border: "1px solid #D8D8D8",
        borderRadius: "12px",
        p: "12px",
        pb: 0,
      }}
    >
      <Box sx={{ margin: "0 auto" }}>
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            color: `${theme?.customSettings?.themeColor}`,
            fontWeight: "500",
            fontSize: "18px",
            //paddingTop: "20px",
            lineHeight: "100%",
            letterSpacing: "0.4px",
          }}
        >
          Delay Analysis
        </Typography>
        <Typography
          variant="body2"
          sx={{ mb: 1, color: "#182A4E80" }}
        >
          Delays to prioritize resolution and maintain schedule integrity
        </Typography>
      </Box>
      <Grid2 container>
        <Grid2 size={{ xs: 12, md: 7 }}>
          {isLoading ? (
            [...Array(3)].map((_, idx) => (
              <Grid2
                container
                direction="row"
                spacing={1}
                key={idx}
                style={{ marginLeft: "30px", paddingTop: "10px" }}
              >
                <Box
                  sx={{
                    width: "25px",
                    height: "25px",
                    borderRadius: "0",
                    backgroundColor: "#eee",
                    marginTop: "5px",
                  }}
                />
                <Skeleton
                  variant="text"
                  width={100}
                  height={24}
                  sx={{ marginLeft: 1 }}
                />
                <Skeleton
                  variant="text"
                  width={30}
                  height={28}
                  sx={{ marginLeft: 2 }}
                />
              </Grid2>
            ))
          ) : !isLoading && analysisData.length === 0 ? (
            <Typography
              sx={{
                padding: 2,
                fontSize: "14px",
                color: "#999",
                whiteSpace: "nowrap",
              }}
            >
              No data available to display the chart.
            </Typography>
          ) : (
            analysisData.map((item, idx) => (
              <Grid2
                container
                direction="row"
                spacing={1}
                key={idx}
                style={{
                  marginLeft: "10px",
                  paddingTop: idx === 0 ? "10px" : "0px",
                }}
              >
                <Typography style={{ padding: "8px", width: "25px" }}>
                  <span className={item?.className}></span>
                </Typography>
                <Typography
                  style={{
                    padding: "9px",
                    width: "140px",
                    color: "#000000",
                    fontSize: "14px",
                    fontWeight: "500",
                    opacity: "0.5",
                    lineHeight: "100%",
                  }}
                >
                  {item?.label}
                </Typography>
                <Typography
                  style={{
                    padding: "6px",
                    width: "30px",
                    color: "#415385",
                    fontSize: "20px",
                    fontWeight: "700",
                    lineHeight: "100%",
                    opacity: "0.9",
                  }}
                >
                  {item?.count}
                </Typography>
              </Grid2>
            ))
          )}
        </Grid2>
        <Grid2 size={{ xs: 12, md: 5 }}>
          <Typography style={{ marginTop: "0px", marginLeft: "-50px" }}>
            {isLoading ? (
              <Skeleton
                variant="circular"
                width={150}
                height={150}
                sx={{ marginLeft: "50px !important", marginBottom: "20px" }}
              />
            ) : (
              <PieChart highcharts={Highcharts} options={options} />
            )}
          </Typography>
        </Grid2>
      </Grid2>
      {open && (
        <ClearancePopup
          title="Delay Analysis"
          value={popupData.value}
          status="Delaydays"
          onOpen={open}
          onClose={() => setOpen(false)}
        />
      )}
    </Box>
  );
};

export default DelayAnalysisGraph;
